import { GetStaticPaths, GetStaticProps } from 'next';
import {
  NavigationItemFragment,
  PageDetailFragment,
  SeasonFragment,
  TournamentCategoryItemFragment,
  UnionFragment,
} from '@amf/shared/types/graphql';
import { sdk } from '@amf/shared/lib/graphQlClient';
import {
  getNavigation,
  getPartners,
  NavigationProps,
  NavigationType,
} from '@amf/shared/utils/navigation';
import { getContentDescription, getContentImageUrl } from '@amf/shared/utils/content';
import Container from '@amf/shared/components/layout/Container';
import Content from '@amf/shared/components/content/Content';
import { getLanguageLocale } from '@amf/shared/utils/languages';
import LeagueTableDashboardGrid from '@amf/shared/components/dashboard/LeagueTableDashboardGrid';
import MatchDashboardList from '@amf/shared/components/dashboard/MatchDashboardList';
import PlayerDashboardGrid from '@amf/shared/components/dashboard/PlayerDashboardGrid';
import React from 'react';

import Layout from 'components/layout/Layout';
import JoinPlayerSend from 'components/join/JoinPlayerSend';
import JoinRefereeSend from 'components/join/JoinRefereeSend';
import JoinTeamSend from 'components/join/JoinTeamSend';
import JoinLeagueSend from 'components/join/JoinLeagueSend';
import Meta from 'components/Meta';
import { AppLinks } from 'utils/links';
import ReprePromote from 'components/repre/ReprePromote';

interface StaticPageProps extends NavigationProps {
  page: PageDetailFragment;
  unions: UnionFragment[];
  categories: TournamentCategoryItemFragment[];
  seasons: SeasonFragment[];
  joinPlayer?: boolean;
  joinReferee?: boolean;
  joinLeague?: boolean;
  joinTeam?: boolean;
  promoteRepre?: boolean;
}

export default function StaticPage({
  page,
  navigation,
  partners,
  seasons,
  joinPlayer,
  joinReferee,
  joinTeam,
  joinLeague,
  promoteRepre,
  unions,
  categories,
}: StaticPageProps) {
  const imageUrl = getContentImageUrl(page.content);
  const description = getContentDescription(page.content);
  return (
    <Layout navigation={navigation} partners={partners}>
      <Meta title={page.title} image={imageUrl} description={description} />
      <Container>
        {seasons.length > 0 && (
          <>
            <LeagueTableDashboardGrid
              season={seasons[0]}
              prefixes={{ team: AppLinks.team, table: AppLinks.table, match: AppLinks.match }}
            />
            <MatchDashboardList
              season={seasons[0]}
              prefixes={{ match: AppLinks.match, team: AppLinks.team, matches: AppLinks.matches }}
            />
            <PlayerDashboardGrid
              season={seasons[0]}
              prefixes={{ player: AppLinks.player, stats: AppLinks.stats }}
            />
          </>
        )}
        <h1>{page.title}</h1>
        <Content content={page.content} />
        {joinPlayer && <JoinPlayerSend unions={unions} />}
        {joinReferee && <JoinRefereeSend unions={unions} />}
        {joinTeam && <JoinTeamSend unions={unions} categories={categories} />}
        {joinLeague && <JoinLeagueSend />}
        {promoteRepre && <ReprePromote />}
      </Container>
    </Layout>
  );
}

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  let paths: { params: { path: string[] }; locale: string }[] = [];

  for (const locale of locales ?? []) {
    const navigation = await getNavigation(getLanguageLocale(locale));
    navigation?.forEach(item => {
      if (item.type === NavigationType.link) {
        return;
      }

      if (item.type === NavigationType.page) {
        paths.push({ params: { path: [item.path] }, locale });
        return;
      }

      for (const itemChild of item.children ?? []) {
        if (!itemChild) {
          continue;
        }

        if (itemChild.type === NavigationType.link) {
          continue;
        }

        paths.push({ params: { path: itemChild.path.split('/') }, locale });
      }
    });
  }

  return { paths, fallback: 'blocking' };
};

export const getStaticProps: GetStaticProps<StaticPageProps> = async ({ locale, params }) => {
  const { path } = params as { path: string[] };
  const slug = path.slice(-1).pop();
  if (!slug) {
    return {
      notFound: true,
    };
  }

  let navigation: NavigationItemFragment[];
  let page: PageDetailFragment | null | undefined;
  try {
    const data = await sdk.StaticPagePage({
      union: process.env.NEXT_PUBLIC_UNION,
      locale: getLanguageLocale(locale ?? 'cz'),
      path: slug,
    });
    navigation = data.navigation;
    page = data.page;
  } catch {
    return {
      notFound: true,
    };
  }

  const { unions } = await sdk.UnionList({
    externalRegistration: null,
    simplified: false,
  });

  const { tournamentCategoryList } = await sdk.TournamentCategoryList({
    union: process.env.NEXT_PUBLIC_UNION,
  });

  if (!page) {
    return {
      notFound: true,
    };
  }

  let seasons: SeasonFragment[] = [];
  if (page.path === process.env.NEXT_PUBLIC_CNP) {
    const data = await sdk.SeasonList({ union: process.env.NEXT_PUBLIC_UNION });
    seasons = data.seasons;
  }

  return {
    props: {
      navigation,
      page,
      unions,
      categories: tournamentCategoryList,
      seasons,
      joinPlayer: slug === process.env.NEXT_PUBLIC_JOIN_PLAYER_SLUG,
      joinReferee: slug === process.env.NEXT_PUBLIC_JOIN_REFEREE_SLUG,
      joinLeague: slug === process.env.NEXT_PUBLIC_JOIN_LEAGUE_SLUG,
      joinTeam: slug === process.env.NEXT_PUBLIC_JOIN_TEAM_SLUG,
      promoteRepre: slug === process.env.NEXT_PUBLIC_REPRE_PROMOTE_SLUG,
      partners: await getPartners(),
    },
    revalidate: 10,
  };
};
